import { useEffect } from 'react';

declare const __DEPLOY_VERSION__: string;
export const DEPLOY_VERSION = typeof __DEPLOY_VERSION__ !== 'undefined' ? __DEPLOY_VERSION__ : 'dev';

async function reloadOnce(targetVersion: string) {
  // sessionStorage guard: never reload-loop if something goes wrong
  const key = `__reloaded_for_${targetVersion}`;
  if (sessionStorage.getItem(key)) return;
  sessionStorage.setItem(key, '1');

  if ('caches' in globalThis) {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }

  // cache-busting param so the browser fetches fresh HTML even if HTTP
  // cache headers haven't expired yet
  const url = new URL(globalThis.location.href);
  url.searchParams.set('_v', targetVersion);
  globalThis.location.href = url.toString();
}

/**
 * Hard-refresh on deploy (Terravo pattern, minus its service-worker layers):
 *
 * Layer 1 — tab-focus check: compares the X-App-Version response header
 *   against the version baked into this bundle; catches long-idle tabs.
 * Layer 2 — SSE deploy stream: the server sends its version on connect, so
 *   when a deploy replaces the server, EventSource reconnects to the new
 *   one and every open tab reloads immediately.
 */
export function useAutoUpdate() {
  useEffect(() => {
    if (DEPLOY_VERSION === 'dev') return; // local dev: never auto-reload

    // strip the cache-busting param once the fresh bundle has loaded
    const startUrl = new URL(globalThis.location.href);
    if (startUrl.searchParams.has('_v')) {
      startUrl.searchParams.delete('_v');
      globalThis.history.replaceState(null, '', startUrl.toString());
    }

    async function onFocus() {
      try {
        const r = await fetch('/api/health', { cache: 'no-store' });
        const v = r.headers.get('X-App-Version');
        if (v && v !== 'dev' && v !== DEPLOY_VERSION) reloadOnce(v);
      } catch {
        /* offline — skip */
      }
    }
    globalThis.addEventListener('focus', onFocus);

    let es: EventSource | undefined;
    if (typeof EventSource !== 'undefined') {
      es = new EventSource('/api/deploy-events');
      es.onmessage = (e) => {
        try {
          const { version } = JSON.parse(e.data) as { version: string };
          if (version !== 'dev' && version !== DEPLOY_VERSION) reloadOnce(version);
        } catch {
          /* malformed message — ignore */
        }
      };
    }

    return () => {
      globalThis.removeEventListener('focus', onFocus);
      es?.close();
    };
  }, []);
}
